import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@chakra-ui/react';
import { useSnackbar } from '@components/snackbar';
import {
  AlertIcon,
  BellIcon,
  BoardIcon,
  ChevronRightIcon,
  CollapseIcon,
  DownArrowIcon,
  ExpandIcon,
  LeftArrowIcon,
  MoreIcon,
  RightArrowIcon,
  ShareTwoIcon,
  SplitIcon,
  StopCircle,
} from '@sketchnote/icons';
import {
  Avatar,
  AvatarGroup,
  Button,
  IconTextBtn,
  InviteUsers,
  InviteUsersProps,
  MoreActionMenu,
} from '@sketchnote/shared-components';
import Tippy from '@tippyjs/react';
import cn from 'classnames';
import { frontendHostUrl } from 'config';
import { Emoji } from 'emoji-mart';
import Link from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';
import { API } from 'src/api';
import Analytics from 'src/api/services/analytics/analytics';
import { useFileExploreStore } from 'src/components/file-explore';
import { TimerNavbar } from 'src/components/navbar/TimerWidget/TimerNavbar';
import { ProfilePopover } from 'src/components/profile-popup';
import { useSidebarStore } from 'src/components/sidebar/store';
import { PLANRULES } from 'src/constants/plans';
import { useBoardStore } from 'src/features/boards';
import { EmojiPopover } from 'src/features/EmojiPopover';
// import { StopTimer } from 'src/features/tasks/components/stopTimer';
import { TimerTracker } from 'src/features/tasks/components/TimeTrackerDetails';
import { useAuth } from 'src/shared/hooks/use-auth';
import { useLayout } from 'src/shared/hooks/use-layout';
import { getAllOrgMembers } from 'src/utils/getMemberDetails';
import useSWR from 'swr';

import { useFileAccess } from '../hooks/useFileAccess';
import { useFileStore } from '../store/useFileStore';
import { ManageViews } from './ManageViews';
import { PublicShare } from './PublicShare';
import { SelectFileView } from './select-file-view';

const { useState, useEffect, useCallback, useRef, useMemo } = React;

export const FileNavbar = () => {
  const authData = useAuth((s) => s.userDetails);
  const {
    closeExploreMenu,
    setIsExploreOpen,
    isExploreOpen,
    setFileOption,
    setHasExpolreMenu,
    isOverviewOpen,
    setIsOverviewOpen,
    closeOverviewMenu,
  } = useFileExploreStore();
  const [icon, setIcon] = useState<any>();
  const [fileName, setFileName] = useState('');

  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isManageViewsOpen, setIsManageViewsOpen] = useState(false);
  const [isNotificationModal, setNotificationModal] = useState(false);

  const [isSelectViewModalOpen, setIsSelectViewModalOpen] = useState(false);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const { data: guestList } = useSWR('/user/guests', API.get);
  const [renameFile, setRenameFile] = useState(false);

  const fetchSidebar = useSidebarStore((s) => s.fetchSidebar);
  const router = useRouter();
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;
  const fileId = router?.query?.slug as string;

  const plan = authData?.plan?.type || '';
  const {
    isSidebarOpened,
    notificationCount,
    onOpenNotifications,
    onToggleSideBar,
    splitView,
    setSplitView,
    setSplitViewFirstFile,
    setSplitViewSecondFile,
    onFetchNotificationCount,
  } = useLayout();

  const [openSnackbar] = useSnackbar();
  const currentFile = useFileStore(
    useCallback((s) => s.files[router.query.slug as string], [router.query.slug as string])
  );
  useEffect(() => {
    if (currentFile?.overview?.open === true) {
      setIsOverviewOpen(true);
    }
  }, [currentFile?.overview?.open]);

  const { isReadOnly, isFullAccess } = useFileAccess();

  const isPrivate = useMemo(() => {
    return (
      currentFile?.workspace &&
      Object.getOwnPropertyNames(currentFile?.workspace).length === 0 &&
      currentFile?.shared?.length === 0
    );
  }, [currentFile]);

  const onUpdateFileNameAndIcon = useFileStore((s) => s.onUpdateFileNameAndIcon);

  const fetchFile = useFileStore((s) => s.fetchFile);
  const { isRenameFileOpened, setIsRenameFileOpened } = useFileStore();

  useEffect(() => {
    setFileName(currentFile?.name || '');
    setIcon(currentFile?.icon);
    return () => setFileName('');
  }, [currentFile]);

  useEffect(() => {
    if (authData?.authentication === 'authorised') {
      onFetchNotificationCount();
    }
  }, [authData?.authentication]);

  function handleBackwardNavigation() {
    router.back();
  }

  function handleForwardNavigation() {
    window.history.forward();
  }

  const toggleEmojiPicker = (value?: boolean) => () => {
    setIsEmojiPickerOpen(value ?? !isEmojiPickerOpen);
  };

  const handleManageView = () => {
    if (!isPrivate) setIsManageViewsOpen(true);
    else setNotificationModal(true);
  };

  const handleInviteUsers: InviteUsersProps['onSubmit'] = async (data) => {
    setIsShareOpen(false);
    const payload = {
      access: data.access.value,
      add: {
        memberIds: data.items.filter((obj) => !obj.isNew).map((obj) => obj.memberId),
        emails: data.items.filter((obj) => obj.isNew).map((obj) => obj.email),
      },
    };
    await API.put(`/files?id=${fileId}`, payload);
    // memberRevalidate();
    fetchFile({ id: fileId });
  };

  const handleUpdateNameAndIcon = async () => {
    setIsRenameFileOpened(false);
    if (currentFile?.id) {
      const response: any = await onUpdateFileNameAndIcon({
        fileId: currentFile?.id,
        icon,
        name: fileName,
      });
      if (response) {
        if (currentFile?.pin) fetchSidebar(mode);
        openSnackbar({
          text: `${fileName} has been updated`,
          snackType: 'standard',
          position: 'bottom-center',
          duration: 1500,
        });
        closeExploreMenu();
      }
    }
  };
  useEffect(() => {
    closeExploreMenu();
    if (currentFile?.overview && currentFile?.overview?.open !== true) closeOverviewMenu();
  }, [router.query]);

  const handleOnChangeAccess = (member) => async (access) => {
    if (access.value === 'remove') {
      await API.put(`/files?id=${fileId}`, { remove: member.member.memberId });
    } else {
      const payload = {
        access: access.value,
        edit: [member.member.memberId],
      };
      await API.put(`/files?id=${fileId}`, payload);
    }
    // memberRevalidate();
    fetchFile({ id: fileId });
  };

  const handleExploreOpen = () => {
    if (isExploreOpen) {
      closeExploreMenu();
    } else {
      setIsExploreOpen(true);
      setHasExpolreMenu(true);
      Analytics.track('Explore Click');
    }
  };
  const handleOverviewOpen = () => {
    if (isOverviewOpen) {
      closeOverviewMenu();
    } else {
      setIsOverviewOpen(true);
      Analytics.track('Overview Click');
    }
  };

  const splitTheView = () => {
    setSplitView(!splitView);
    const activeSplitFile =
      currentFile?.internalFiles && currentFile?.internalFiles.length
        ? {
            view:
              currentFile?.internalFiles[0].type === 'board'
                ? currentFile?.internalFiles[0].view
                : currentFile?.internalFiles[0].view,
            id: currentFile?.internalFiles[0].id,
            name: currentFile?.name?.length ? currentFile?.name : 'Untitled',
          }
        : {
            view: '',
            id: '',
            name: '',
          };

    setSplitViewFirstFile({
      view: router.query.type as string,
      id: router.query.view as string,
      name: '',
    });
    setSplitViewSecondFile(activeSplitFile);
  };

  const handleFileNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(event.target.value);
  };

  const handleUpdateIcon = ({ icon }: { icon: any }) => {
    setIcon(icon);
    setIsEmojiPickerOpen(false);
  };

  const otherTypes =
    currentFile?.internalFiles?.map((file) => ({
      view: file.type === 'board' ? file.view : file.type,
      id: file.id,
    })) || [];

  const board = ['kanban', 'list', 'calendar', 'gantt', 'timesheet', 'document'];

  const visibleFiles = currentFile?.file_hidden
    ? [...otherTypes]
    : [
        {
          view:
            currentFile?.primaryFile?.type === 'board'
              ? currentFile?.primaryFile.view
              : currentFile?.primaryFile?.type,
          id: currentFile?.primaryFile?.id,
        },
        ...otherTypes,
      ];

  const secondFile = visibleFiles
    .filter((grp) => {
      if (board.includes(router.query.type as string)) {
        return !board.includes(grp.view as string);
      }
      return true;
    })
    .filter((grp) => grp.id !== (router.query.view as string))
    .filter((value, index, self) => {
      return self.indexOf(value) === index;
    });

  const isSplittable = () => {
    if (currentFile?.file_hidden) {
      if (secondFile && secondFile.length > 1) return true;
    } else {
      if (secondFile && secondFile.length) return true;
    }
    return false;
  };

  const canSetShareAccessLevels = React.useMemo(() => {
    return authData?.plan.type ? !PLANRULES.shareaccesslevels[authData?.plan.type] : false;
  }, []);

  return currentFile?.authorisation ? (
    <div className="flex justify-between border-gray-200 py-8 px-25 z-40 w-full">
      <div className="flex items-center">
        {!isSidebarOpened ? (
          <Button
            onClick={onToggleSideBar}
            className="pl-0 pr-0 mr-32 cursor-pointer"
            status="ghost"
            iconLeft={<ExpandIcon wrapperClass="text-gray-500" size={16} />}
          />
        ) : (
          <span />
        )}
        <div className="flex items-center mt-3">
          <button onClick={handleBackwardNavigation} className="mr-18 cursor-pointer">
            <LeftArrowIcon wrapperClass="text-gray-500" />
          </button>
          <button onClick={handleForwardNavigation} className="cursor-pointer mr-20">
            <RightArrowIcon wrapperClass="text-gray-500" />
          </button>
        </div>

        <div className="font-medium whitespace-no-wrap items-center flex">
          <div className="mobile:hidden bigScreen:flex">
            {currentFile?.workspace?.id ? (
              <div className="flex items-center">
                <Link href={`/${mode}/${orgId}/w/${currentFile?.workspace.id}`}>
                  <a>
                    <div className="flex mt-1 ">
                      {currentFile?.workspace?.icon?.id &&
                      currentFile?.workspace?.icon?.type === 'image' ? (
                        <span className="h-15 w-15">
                          <img
                            className="w-full h-full object-center object-cover"
                            src={currentFile.workspace.icon.id}
                          />
                        </span>
                      ) : (
                        <span className="">
                          <Emoji
                            emoji={
                              (currentFile?.workspace?.icon?.id as any) || {
                                id: 'file_folder',
                              }
                            }
                            set="apple"
                            size={15}
                          />
                        </span>
                      )}

                      <span className="inline-block max-w-300 truncate text-sm  ml-2 items-center ">
                        {currentFile.workspace.name}
                      </span>
                    </div>
                  </a>
                </Link>
                <ChevronRightIcon className=" text-gray-500 mx-4" />
              </div>
            ) : (
              <div className="flex items-center">
                <BoardIcon wrapperClass="text-primary-300 mr-4 text-sm" />
                <div className="mb-2">
                  {currentFile?.shared?.length ? (
                    <Link href={`/${mode}/${orgId}/files?list=shared`}>
                      <a>
                        <span className="text-sm">Shared Files</span>
                      </a>
                    </Link>
                  ) : (
                    <Link href={`/${mode}/${orgId}/files?list=private`}>
                      <a>
                        <span className="text-sm">Private Files</span>
                      </a>
                    </Link>
                  )}
                </div>
                <ChevronRightIcon className="text-gray-500 mx-4" />
              </div>
            )}
          </div>

          <div className="flex items-center mb-1">
            {currentFile?.icon?.id && currentFile?.icon?.type === 'image' ? (
              <span className="h-15 w-15">
                <img
                  className="w-full h-full object-center object-cover"
                  src={currentFile.icon.id}
                />
              </span>
            ) : (
              <Emoji
                emoji={
                  (currentFile?.icon?.id as any) || {
                    id: 'bulb',
                  }
                }
                set="apple"
                size={15}
              />
            )}
            <div
              className="flex relative items-center"
              onMouseEnter={() => setRenameFile(true)}
              onMouseLeave={() => setRenameFile(false)}
            >
              <span className="max-w-300 truncate ml-2 text-sm inline-block">
                {currentFile?.name && currentFile?.name.length > 0 ? currentFile?.name : 'Untitled'}
              </span>

              {(renameFile || isRenameFileOpened) && !isReadOnly ? (
                <Popover
                  placement="bottom"
                  onClose={() => setIsRenameFileOpened(false)}
                  onOpen={() => setIsRenameFileOpened(true)}
                  isOpen={isRenameFileOpened}
                >
                  <PopoverTrigger>
                    <button className="bg-white shadow-sm rounded-sm px-8 absolute -right-10 top-2">
                      <MoreIcon />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    placement="bottom"
                    className="bg-white shadow-sm rounded-lg mt-10 p-16"
                  >
                    <div className="flex mb-8">
                      <div className="mr-6">
                        <Popover
                          offset={[180, 10]}
                          isOpen={isEmojiPickerOpen}
                          onClose={toggleEmojiPicker(false)}
                          onOpen={toggleEmojiPicker(true)}
                          modifiers={[
                            { name: 'eventListeners', options: { scroll: isEmojiPickerOpen } },
                          ]}
                        >
                          <PopoverTrigger>
                            <button className="border bg-gray-100 mr-8 flex items-center justify-center h-48 w-48 rounded">
                              {icon?.type === 'image' ? (
                                <img className="w-25 h-25 m-auto" src={icon?.id} />
                              ) : (
                                <Emoji
                                  emoji={
                                    icon || {
                                      id: 'file_folder',
                                    }
                                  }
                                  set="apple"
                                  size={25}
                                />
                              )}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent>
                            <EmojiPopover onChangeIcon={handleUpdateIcon} />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <input
                        onChange={handleFileNameChange}
                        className="h-48 p-14 mb-10 bg-gray-100 rounded border w-full"
                        // style={{ width: '35.6rem' }}
                        value={fileName}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleUpdateNameAndIcon();
                        }}
                      />
                    </div>
                    <button
                      onClick={handleUpdateNameAndIcon}
                      className="bg-blue-500 text-white rounded-sm w-full text-center text-sm font-medium py-6"
                    >
                      Update name
                    </button>
                  </PopoverContent>
                </Popover>
              ) : (
                <span />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center">
        <div
          className={cn({
            // 'h-0 opacity-0': !showMore,
            'transition-all duration-200 flex pl-10 ': true,
          })}
        >
          <div className={cn({ 'flex items-center mobile:hidden bigScreen:flex': true })}>
            {!currentFile?.workspace?.id &&
            currentFile?.shared &&
            currentFile?.shared?.length > 0 ? (
              <div className="flex">
                <AvatarGroup maxCount={3} className="mr-13">
                  {currentFile?.shared.map(({ member }, index) => (
                    <Avatar
                      size="sm1"
                      key={`${member.firstname}-${index}`}
                      name={member.firstname}
                      type={member.avatar ? 'photo' : 'initials'}
                      src={member.avatar}
                      wrapperClassName="-mr-8 border-2 border-white  transform transition duration-500 hover:scale-125"
                    />
                  ))}
                </AvatarGroup>
                {currentFile?.shared?.length > 3 && (
                  <Avatar
                    size="sm1"
                    name={currentFile?.shared?.length - 3}
                    tooltip={false}
                    type={'initials'}
                    src={''}
                    wrapperClassName="-ml-18 border-1 border-white  transform transition duration-500 hover:scale-125"
                  />
                )}
              </div>
            ) : null}
          </div>
          {currentFile?.primaryFile?.type === 'board' && currentFile?.primaryFile?.view === 'list' && (
            <Button
              status="ghost"
              className="pb-4 pt-5 ml-8 mr-4 text-sm text-gray-999"
              onClick={handleOverviewOpen}
              iconLeft={<CollapseIcon wrapperClass="text-primary-500" size={18} />}
            >
              Overview
            </Button>
          )}

          {authData?.authentication === 'authorised' &&
            authData?.memberInformation?.role !== 'guest' && <TimerNavbar />}
          <div className="mobile:hidden laptop:block">
            <Popover>
              <PopoverTrigger>
                <IconTextBtn
                  iconLeft={
                    // <ShareIcon wrapperClass="inline-block pb-1" className="pr-8" size={22} />
                    <ShareTwoIcon className="pr-8" size={22} />
                  }
                  color="gray"
                  className="py-4 px-8 text-sm"
                >
                  Share
                </IconTextBtn>
              </PopoverTrigger>
              <PopoverContent>
                <div
                  className="rounded-lg min-w-400 bg-white"
                  style={{ boxShadow: '0px 2px 12px rgba(19, 23, 32, 0.08)' }}
                >
                  {!currentFile?.workspace?.id ? (
                    <div>
                      <div className="py-20 flex w-full px-20 border-b border-gray-100 items-center">
                        <span className="mr-8 text-sm">To:</span>
                        <input
                          className="w-full"
                          placeholder="Add a name or email"
                          onFocus={() => setIsShareOpen(true)}
                        />
                      </div>
                      {currentFile?.shared?.length > 0 && (
                        <div className="px-20 py-10 bg-gray-100 text-xs text-gray-600">
                          Shared with
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="px-20 py-10  text-sm text-gray-600">
                      All members of this workspace already have access to this file
                    </div>
                  )}
                  <div className="flex justify-end">
                    <Tippy
                      content={'Only the users with full access can add manage views'}
                      disabled={isFullAccess}
                    >
                      <button
                        onClick={isFullAccess ? handleManageView : undefined}
                        className={` px-20 py-2 border-t border-b font-medium border-gray-100 text-sm text-right ${
                          isPrivate || !isFullAccess ? 'text-gray-400' : 'text-blue-500'
                        }`}
                      >
                        Manage views
                      </button>
                    </Tippy>
                  </div>
                  {!currentFile?.workspace?.id &&
                    currentFile?.shared?.map((item) => (
                      <div
                        className="flex items-center justify-between px-20 py-16"
                        key={item.member.memberId}
                      >
                        <div className="flex items-center w-full">
                          <Avatar
                            name={item.member.firstname}
                            type="photo"
                            src={item.member.avatar}
                          />

                          {item.invited ? (
                            <div className="ml-12">
                              <p className="text-sm">{item.member.email}</p>
                              <p className="text-xs">
                                <span className="font-medium">{item.member.role}</span> -{' '}
                                <span className="text-xs italic">Invite pending</span>
                              </p>
                            </div>
                          ) : (
                            <div className="ml-12">
                              <p className="text-sm font-medium">{item.member.firstname}</p>
                              <p className="text-xs">
                                <span className=" font-medium">{item.member.role}</span> -{' '}
                                <span className="">{item.member.email}</span>
                              </p>
                            </div>
                          )}
                        </div>
                        <Tippy
                          className="w-auto h-auto bg-gray-999 z-50"
                          content={'Only the users with full access can add other members'}
                          arrow={true}
                          disabled={isFullAccess}
                        >
                          <div>
                            <MoreActionMenu
                              icon={
                                <span className="flex items-center text-gray-600 text-sm">
                                  {item.access} <DownArrowIcon wrapperClass="ml-8" />
                                </span>
                              }
                              disabled={!isFullAccess || canSetShareAccessLevels}
                              onClickMenuItem={handleOnChangeAccess(item)}
                              menuItems={[
                                { label: 'Full access', value: 'full' },
                                { label: 'Edit access', value: 'edit' },
                                { label: 'View access', value: 'view' },
                                // { label: 'Comment access', value: 'comment' },
                                { label: 'Remove access', value: 'remove' },
                              ]}
                            />
                          </div>
                        </Tippy>
                      </div>
                    ))}

                  {!isReadOnly && currentFile?.primaryFile?.type !== 'form' ? (
                    <PublicShare
                      publicURL={`${frontendHostUrl}${mode}/${orgId}/files/${
                        router.query.slug
                      }/public?view=${currentFile?.primaryFile?.id}&type=${
                        currentFile?.primaryFile?.type === 'board'
                          ? currentFile?.primaryFile?.view
                          : currentFile?.primaryFile?.type
                      }`}
                    />
                  ) : (
                    <span />
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>
          {isSplittable() ? (
            <Tippy
              className="w-auto h-auto bg-gray-999 tippy"
              content={splitView ? 'Exit Split Screen' : 'Split Screen'}
              placement="bottom"
              arrow
            >
              <div
                className="flex items-center justify-center px-5 cursor-pointer"
                style={{ color: splitView ? 'black' : '#878A92' }}
                onClick={splitTheView}
              >
                <SplitIcon />
              </div>
            </Tippy>
          ) : (
            <span />
          )}
        </div>

        {/* {boardsStore?.timer?.isRunning && (
          <Tippy
            interactive
            className="w-221 h-auto bg-blue-500 tippy text-sm"
            arrow={false}
            content={
              <div>
                <div>
                  Timer running for
                  <br />
                  <br />
                  <span className="font-medium">{boardsStore?.timer?.boardTitle}</span> &gt;{' '}
                  <span className="font-medium">{boardsStore?.timer?.taskTitle}</span>
                </div>

                <Link
                  href={`/${mode}/${orgId}/tasks/${boardsStore?.timer?.task}?routeBack=default`}
                >
                  <a className="mt-16 block">
                    Go to task <RightArrowIcon className="text-sm text-white ml-8 pt-4" />
                  </a>
                </Link>
              </div>
            }
            placement={'bottom'}
          >
            <button onClick={() => setIsStopTimerModalOpen(true)}>
              <div className="flex items-center w-100 ml-16 justify-center">
                <StopCircle className="mr-4 cursor-pointer hover:opacity-75" size={16} />
                <TimerTracker
                  className="text-xs w-80 text-gray-500"
                  startTime={boardsStore?.timer?.startTime}
                  timeTrackedTaskId={boardsStore?.timer?.task}
                  isRunning={boardsStore?.timer?.isRunning}
                />
              </div>
            </button>
          </Tippy>
        )} */}

        <Button status="ghost" className="pb-4 pt-5 ml-8 text-sm" onClick={handleExploreOpen}>
          File options
        </Button>

        {authData?.authentication === 'authorised' ? (
          <button
            onClick={onOpenNotifications}
            className="border gray-500 rounded-full ml-8 mr-4 relative"
          >
            {notificationCount && notificationCount > 0 ? (
              <span
                className="text-xss text-white rounded-full absolute -right-5 -top-5 w-18 h-18 pt-1"
                style={{ backgroundColor: '#375DFB' }}
              >
                {notificationCount > 9 ? '9+' : notificationCount}
              </span>
            ) : (
              <></>
            )}
            <div className="w-30 h-30 rounded-full bg-gray-100 m-1.5">
              <BellIcon className="mt-6" />
            </div>
          </button>
        ) : (
          <></>
        )}

        <ProfilePopover showName={false} />

        <Modal isOpen={isShareOpen} onClose={() => setIsShareOpen(false)}>
          <ModalOverlay />
          <ModalContent
            maxWidth="50rem"
            overflow="auto"
            borderRadius="0.8rem"
            className="bg-white static rounded-lg"
            margin="auto"
          >
            <ModalBody className="flex" p="0">
              <div className="w-full">
                <InviteUsers
                  members={
                    getAllOrgMembers()
                      ?.filter((obj) => obj.memberId !== authData?.memberInformation?.memberId)
                      .map((obj) => ({
                        firstname: obj.firstname,
                        email: obj.email,
                        type: obj.role,
                        avatar: obj.avatar,
                        memberId: obj.memberId,
                      })) || []
                  }
                  accessLevels={[
                    { label: 'Full access', value: 'full' },
                    { label: 'Edit access', value: 'edit' },
                    { label: 'View access', value: 'view' },
                    // { label: 'Comment access', value: 'comment' },
                  ]}
                  onSubmit={handleInviteUsers}
                  hasAccessLevels={!canSetShareAccessLevels}
                  userInviteLimit={PLANRULES.guests[plan]}
                  guestCount={guestList?.data?.data?.length}
                  isFreeTrial={!!authData?.plan?.trial}
                />
              </div>
            </ModalBody>
          </ModalContent>
        </Modal>
        {/* <Modal isOpen={isStopTimerModalOpen} onClose={() => setIsStopTimerModalOpen(false)}>
          <ModalOverlay />
          <ModalContent
            style={{
              maxWidth: '46rem',
              padding: '32px',
              marginTop: '20rem',
              borderRadius: '8px',
            }}
          >
            <ModalCloseButton />

            <StopTimer
              isRunning={boardsStore?.timer?.isRunning}
              startTime={boardsStore?.timer?.startTime}
              taskId={boardsStore?.timer?.task}
              taskName={boardsStore?.timer?.taskTitle}
              fileName={boardsStore?.timer?.boardTitle}
              type={'board-navbar'}
            />
          </ModalContent>
        </Modal> */}
        {/* isManageViewsOpen */}
        <Modal isOpen={isManageViewsOpen} onClose={() => setIsManageViewsOpen(false)}>
          <ModalOverlay />

          <ModalContent
            style={{
              maxWidth: '53rem',
              padding: '32px',
              marginTop: '20rem',
              borderRadius: '8px',
            }}
          >
            <ManageViews onClose={() => setIsManageViewsOpen(false)} />
          </ModalContent>
        </Modal>
        <Modal isOpen={isNotificationModal} onClose={() => setNotificationModal(false)}>
          <ModalOverlay />

          <ModalContent
            style={{
              maxWidth: '53rem',
              padding: '8px',
              borderRadius: '8px',
              position: 'fixed',
              bottom: '0',
            }}
          >
            <span className="text-gray-600 text-sm">
              This file is private to you. View access can be restricted only for shared files.
            </span>
          </ModalContent>
        </Modal>

        <Modal isOpen={isSelectViewModalOpen} onClose={() => setIsSelectViewModalOpen(false)}>
          <ModalOverlay />
          <ModalCloseButton />
          <ModalContent
            width="auto"
            maxWidth="auto"
            margin="auto"
            borderRadius="0.8rem"
            className="static rounded-lg"
          >
            <ModalBody className="flex items-center justify-center text-left" padding="0px">
              <SelectFileView onClose={() => setIsSelectViewModalOpen(false)} />
            </ModalBody>
          </ModalContent>
        </Modal>
      </div>
    </div>
  ) : (
    <></>
  );
};
